import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

const ServicesPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  const services = [
    {
      id: 'business-cards',
      name: 'Business Cards',
      description: 'Make a lasting first impression with premium business cards. Choose from a variety of paper stocks, finishes, and custom designs.',
      features: ['Standard & Custom Sizes', 'Matte, Gloss & Soft Touch Finishes', 'Spot UV & Foil Options', 'Same-Day Rush Available'],
      image: '/images/business-cards.png',
    },
    {
      id: 'flyers-brochures',
      name: 'Flyers & Brochures',
      description: 'Eye-catching marketing materials that communicate your message effectively.',
      features: ['Single & Multi-Page Options', 'Various Fold Styles', 'High-Quality Color Printing', 'Bulk Discounts Available'],
      image: '/images/flyers-brochures.png',
    },
    {
      id: 'posters-banners',
      name: 'Posters & Banners',
      description: 'Large format printing that makes a big impact for events and advertising.',
      features: ['Up to 60" Wide Format', 'Indoor & Outdoor Materials', 'Vibrant Color Output', 'Mounting & Finishing Options'],
      image: '/images/posters-banners.png',
    },
    {
      id: 'digital-printing',
      name: 'Digital Printing',
      description: 'Fast turnaround digital printing on our Xerox® Versant® 280 Press — ultra HD resolution and vibrant color for short-run jobs.',
      features: ['Xerox Versant 280 Press', 'Up to 2400×2400 dpi', 'Quick Turnaround', 'Variable Data & Short Runs'],
      image: '/images/xerox-versant-280.webp',
    },
    {
      id: 'offset-printing',
      name: 'Offset Printing',
      description: 'Traditional offset printing for high-volume projects with superior color accuracy and Pantone matching.',
      features: ['4-Color Offset Press', 'Pantone Color Matching', 'High Volume Runs', 'Premium Specialty Papers'],
      image: '/images/offset-press.jpg',
    },
    {
      id: 'custom-jobs',
      name: 'Custom Print Jobs',
      description: 'Have something unique in mind? We specialize in custom printing solutions — including die-cutting for shaped cards, packaging, and labels.',
      features: ['Die-Cutting Available', 'Custom Shapes & Sizes', 'Specialty Materials', 'Expert Consultation'],
      image: '/images/die-cutter.jpg',
    },
  ];

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-white paper-texture" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-sm font-medium tracking-widest uppercase text-[#3EACDB]">Our Services</span>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase mt-4">
            Professional<br />
            <span className="text-[#3EACDB]">Printing</span> Services
          </h1>
          <p className="mt-6 text-lg md:text-xl text-zinc-600 max-w-2xl">
            From business essentials to large format displays, we offer comprehensive printing solutions for every need. Quality guaranteed.
          </p>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section className="py-16 md:py-24 bg-zinc-50" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={service.id}
                id={service.id}
                className="service-card bg-white border border-zinc-200 overflow-hidden hover:border-[#3EACDB]/50 flex flex-col scroll-mt-32"
                data-testid={`service-${service.id}`}
              >
                <div className="relative w-full aspect-[4/3] overflow-hidden bg-zinc-100">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 md:p-7 flex flex-col flex-1">
                  <h3 className="font-heading text-2xl font-bold mb-3">{service.name}</h3>
                  <p className="text-zinc-600 mb-5 text-sm leading-relaxed">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-zinc-700">
                        <CheckCircle className="w-4 h-4 text-[#3EACDB] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to="/quote" 
                    className="mt-auto inline-flex items-center text-[#3EACDB] font-semibold hover:underline"
                    data-testid={`quote-${service.id}`}
                  >
                    Get a Quote
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-16 md:py-24 bg-white" data-testid="additional-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold">Additional Services</h2>
            <p className="mt-4 text-zinc-600">More ways we can help with your print needs</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              'Letterheads',
              'Envelopes',
              'Notepads',
              'Postcards',
              'Stickers & Labels',
              'Booklets',
              'Presentation Folders',
              'Menus & Catalogs',
            ].map((item, index) => (
              <div 
                key={item} 
                className="text-center p-6 border border-zinc-200 hover:border-[#3EACDB]/50 transition-colors"
                data-testid={`additional-service-${index}`}
              >
                <p className="font-medium text-zinc-800">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-zinc-900 text-white" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold">
            Don't See What You Need?
          </h2>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto">
            Contact us for custom print solutions. We can accommodate almost any printing project.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/quote" data-testid="services-cta-quote">
              <Button className="h-12 px-8 text-sm font-bold uppercase tracking-wider bg-[#3EACDB] hover:bg-[#2593C4] rounded-sm">
                Request Custom Quote
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <a href="tel:+12129711334" data-testid="services-cta-call">
              <Button variant="outline" className="h-12 px-8 text-sm font-bold uppercase tracking-wider rounded-sm border-2 border-white text-white hover:bg-white hover:text-zinc-900">
                <Phone className="mr-2 w-4 h-4" />
                Call Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
